import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { getCaseStudy, CASE_ORDER } from "../projectsData";
import { Reveal } from "../components/Reveal";
import { PROFILE } from "../data";

const EASE = [0.22, 1, 0.36, 1];

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = getCaseStudy(slug);

  useEffect(() => {
    if (!cs) return;
    const prevTitle = document.title;
    document.title = `${cs.title} — Jesal Sharma`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", cs.tagline);
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [cs]);

  if (!cs) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display text-3xl font-semibold">Case study not found</h1>
        <Link to="/" className="btn-dark">Back home</Link>
      </div>
    );
  }

  const idx = CASE_ORDER.indexOf(slug);
  const next = CASE_ORDER[(idx + 1) % CASE_ORDER.length];
  const nextCs = getCaseStudy(next);

  return (
    <div className="pt-24 md:pt-28 pb-6 px-4 md:px-6">
      <div className="max-w-[980px] mx-auto">
        <button data-testid="case-back" onClick={() => navigate("/")} className="btn-ghost mb-6">
          <span aria-hidden>←</span> Back to work
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <span className="tag">{cs.category}</span>
          <h1 data-testid="case-title" className="font-display font-semibold text-3xl md:text-5xl leading-[1.08] tracking-tight mt-5">
            {cs.title}
          </h1>
          <p className="text-ink-soft text-lg md:text-xl mt-5 max-w-3xl leading-relaxed">{cs.tagline}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.9, ease: EASE }}
          className="mt-9 overflow-hidden rounded-[28px] aspect-[16/9] bg-white flex items-center justify-center p-6 md:p-10"
        >
          <img src={cs.image} alt={cs.title} className="max-w-full max-h-full object-contain" />
        </motion.div>

        {/* meta */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {cs.meta.map((m) => (
            <div key={m.k} className="card p-5">
              <div className="text-ink-soft text-xs">{m.k}</div>
              <div className="font-display font-medium mt-1">{m.v}</div>
            </div>
          ))}
        </div>

        {/* stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {cs.stats.map((s, i) => (
            <div key={i} className="card p-7">
              <div className="font-display font-semibold text-4xl md:text-5xl tracking-tight text-accent">{s.value}</div>
              <div className="text-ink-soft text-sm mt-2 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>

        {/* body sections */}
        <div className="card p-8 md:p-12 mt-8 space-y-10">
          {cs.sections.map((sec, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div>
                <h2 className="font-display font-semibold text-2xl md:text-3xl mb-4">{sec.heading}</h2>
                {sec.type === "text" ? (
                  <p className="text-ink/85 leading-relaxed md:text-lg">{sec.body}</p>
                ) : (
                  <ul className="space-y-3">
                    {sec.items.map((it, j) => (
                      <li key={j} className="flex gap-4 leading-relaxed text-ink/85">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}

          {cs.docLink && (
            <a data-testid="case-doc-link" href={cs.docLink} target="_blank" rel="noreferrer" className="btn-dark">
              {cs.docLabel} <span aria-hidden>↗</span>
            </a>
          )}
        </div>

        {/* next + cta */}
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <Link to={`/project/${next}`} data-testid="case-next" className="group card p-8 flex flex-col justify-between hover:-translate-y-1.5 transition-transform duration-500">
            <span className="text-ink-soft text-sm">Next case study</span>
            <span className="font-display font-semibold text-xl md:text-2xl mt-4 group-hover:text-accent transition-colors">{nextCs.title} →</span>
          </Link>
          <a href={`mailto:${PROFILE.email}`} className="card p-8 flex flex-col justify-between">
            <span className="text-ink-soft text-sm">Like what you see?</span>
            <span className="font-display font-semibold text-xl md:text-2xl mt-4">Let's work together →</span>
          </a>
        </div>
      </div>
    </div>
  );
}
